import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import { Refresh } from '@mui/icons-material';
import {
  DataGridPro,
  GridPagination,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import { ConfirmationModal } from '@components';
import { useStableGridModels } from '@common/hooks';
import { IPartnerTableEntry, usePartnerTable } from './hooks';
import { theme } from '@store';
import { TOOLBAR_FIELD_GAP, toolbarFieldSx } from '@ui';
import { TableNoResultPlaceholder } from '@components';
import {
  PinnedColumnMenu,
  ColumnDensityFilter,
  TableColumnHeightMapper,
} from 'components/TablePreferences';

interface IPartnerTableProps {
  rows: IPartnerTableEntry[];
  totalCount: number;
  onSelectPartner: (partnerId: string) => void;
  loading: boolean;
  selectedRowId: string;
}

export const PartnerTable = ({
  rows,
  totalCount,
  loading,
  onSelectPartner,
  selectedRowId,
}: IPartnerTableProps) => {
  const {
    filters,
    columns,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    handleDownload,
    isDownloading,
    handleResetAll,
    searchInputValue,
    initialSortFilters,
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange,
    apiRef,
  } = usePartnerTable();

  const { paginationModel, sortModel, rowCount } = useStableGridModels({
    page: filters.page,
    limit: filters.limit,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    totalCount,
    loading,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: TOOLBAR_FIELD_GAP,
            alignItems: 'center',
          }}
        >
          <OutlinedInput
            id='searchField'
            placeholder='Search partner'
            onChange={handleSearchChange}
            value={searchInputValue}
            sx={toolbarFieldSx}
            startAdornment={
              <InputAdornment sx={{ padding: 0 }} position='start'>
                <SearchIcon />
              </InputAdornment>
            }
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <ColumnDensityFilter
            handleDensityChange={handleDensityChange}
            selectedValue={density}
          />
          <Divider orientation='vertical' sx={{ marginRight: '0.5rem' }} />
          <IconButton
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{ height: 40, width: 40 }}
          >
            <DownloadIcon sx={{ color: 'primary.main' }} />
          </IconButton>
        </Box>
      </Box>

      <DataGridPro
        apiRef={apiRef}
        sx={{
          maxHeight: 'calc(100vh - 410px)',
          '--DataGrid-overlayHeight': 'calc(100vh - 410px)',
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' },
          '& .selected-row': {
            bgcolor: theme.palette.selectedTableRowBg.main,
            '&:hover': { bgcolor: theme.palette.selectedTableRowBg.main },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        slots={{
          columnMenu: props => (
            <PinnedColumnMenu {...props} pinnedColumns={pinnedColumns} />
          ),
          pagination: props => (
            <Box
              sx={{
                px: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color='primary'
                sx={{ whiteSpace: 'nowrap' }}
                startIcon={<Refresh color='primary' />}
                onClick={() => setIsResetModalOpen(true)}
              >
                RESET VIEW
              </Button>
              <GridPagination {...props} />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => theme.palette.common.white,
              }}
            >
              <TableNoResultPlaceholder />
            </Box>
          ),
        }}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnWidthChange={handleColumnWidthChange}
        getRowHeight={() => TableColumnHeightMapper[density ?? 'comfortable']}
        pageSizeOptions={[10, 25, 50, 100]}
        pagination
        paginationModel={paginationModel}
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        paginationMode='server'
        sortingMode='server'
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        sortModel={sortModel}
        onPaginationModelChange={onPageModelChange}
        onSortModelChange={model => {
          if (
            model[0] &&
            (model[0].field !== filters.sort_by ||
              model[0].sort !== filters.sort_order)
          ) {
            handleChangeOrder(model[0].field, model[0].sort);
          } else {
            handleChangeOrder(
              initialSortFilters.sort_by,
              initialSortFilters.sort_order as GridSortDirection,
            );
          }
        }}
        onRowClick={params => onSelectPartner(params.row.id)}
        getRowClassName={params => {
          const zebra =
            params.indexRelativeToCurrentPage % 2 !== 0 ? 'row-even' : '';
          const selected =
            params.row.id === selectedRowId ? 'selected-row' : '';
          return [zebra, selected].filter(Boolean).join(' ');
        }}
      />
      <ConfirmationModal
        title='Reset table view'
        message='This will restore the default columns, sorting, and filters. Your custom layout and pinned columns will be cleared.'
        closeText='KEEP CURRENT VIEW'
        confirmText='RESET VIEW'
        onConfirm={() => {
          handleResetAll();
          setIsResetModalOpen(false);
        }}
        onClose={() => setIsResetModalOpen(false)}
        isOpen={isResetModalOpen}
        reverseButtonOrder
      />
    </Box>
  );
};
